import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useRenderCounter } from "@/lib/loop-detector";
import { ChevronDown, ChevronUp, Plus, Trash2, AlertCircle, RotateCcw, GitCompare, Loader2, BookOpen, ArrowRight, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { formatAmount } from "@/lib/formatters";
import { STAMMDATEN } from "@/components/project/leistungsverzeichnis-kalkulator";
import type { LibraryPosition } from "@shared/schema";
import type { PhotoLVPosition } from "@/lib/storage";

/**
 * Andere Fotos im selben Projekt, deren Positions-Sets als Vorlage zum
 * Übernehmen angeboten werden. Nur Fotos mit mindestens einer Position
 * gehören rein — alles andere filtert die parent-Komponente heraus.
 */
export interface PhotoSchadenSource {
  photoId: string;
  photoName: string;
  positionen: PhotoLVPosition[];
}

interface PhotoSchadenserfassungProps {
  photoId: string;
  photoName: string;
  description: string;
  photoPositionen: PhotoLVPosition[];
  /** Aus dem Aufmaß errechnete Netto-Fläche dieses Fotos (Vorschlag, optional). */
  measuredArea?: number;
  /**
   * Andere Fotos im Projekt, die bereits Positionen haben — werden im
   * Dialog "Positionen aus anderem Foto übernehmen" angeboten. Ist die
   * Liste leer/undefiniert, wird der Button ausgeblendet.
   */
  otherPhotosWithPositions?: PhotoSchadenSource[];
  onDescriptionChange: (desc: string) => void;
  onPositionenChange: (positionen: PhotoLVPosition[]) => void;
  /**
   * Optional: Springt im Projekt-Editor zu Schritt 4 und hebt die zur
   * übergebenen Position-ID gehörige LV-Zeile kurz farblich hervor.
   * Wird der Handler nicht übergeben, wird der "Im LV anzeigen"-Link
   * nicht gerendert.
   */
  onJumpToLv?: (posId: string) => void;
}

export function PhotoSchadenserfassung({
  photoId,
  photoName,
  description,
  photoPositionen,
  measuredArea,
  otherPhotosWithPositions,
  onDescriptionChange,
  onPositionenChange,
  onJumpToLv,
}: PhotoSchadenserfassungProps) {
  useRenderCounter(`PhotoSchadenserfassung#${photoId}`);
  const [isOpen, setIsOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSelection, setPickerSelection] = useState<Set<string>>(new Set());
  const [pickerSearch, setPickerSearch] = useState("");
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySourceId, setCopySourceId] = useState<string | null>(null);
  const [copyMode, setCopyMode] = useState<"append" | "replace">("append");

  const subtotal = photoPositionen.reduce((sum, p) => (p.variant ? sum : sum + p.qty * p.ep), 0);

  const STANDARD_EINHEITEN = ["m", "m²", "lfm", "Stk", "kg", "Liter", "Psch", "Std"] as const;

  // Datenquelle für den Picker: aktive, freigegebene Einträge aus der
  // Positions-Bibliothek. Identisch zur LV-Kalkulator-Query, damit der Cache
  // geteilt wird und Library-Änderungen sofort beim nächsten Öffnen sichtbar
  // sind.
  const { data: libraryPositions = [], isLoading: isLibraryLoading, isError: isLibraryError } = useQuery<LibraryPosition[]>({
    queryKey: ["/api/library-positions"],
  });

  // Gruppierung nach Sektion in der Reihenfolge/Icon-Definition aus STAMMDATEN.
  // STAMMDATEN dient hier nur noch als Sektions-/Gruppendefinition (Reihenfolge,
  // Icons, Titel) — die Positionen stammen ausschließlich aus der Bibliothek.
  const pickerSections = useMemo(() => {
    const aktive = libraryPositions.filter((lp) => lp.aktiv);
    const q = pickerSearch.trim().toLowerCase();
    return STAMMDATEN.map((s) => ({
      id: s.id,
      icon: s.icon,
      titel: s.titel,
      positionen: aktive
        .filter((lp) => lp.sektionId === s.id)
        .filter((lp) => {
          if (!q) return true;
          const haystack = [lp.name, lp.tag ?? "", lp.unit]
            .join(" ")
            .toLowerCase();
          return haystack.includes(q);
        })
        .map((lp) => ({
          id: `lib-${lp.id}`,
          name: lp.name,
          unit: lp.unit,
          ep: lp.ep,
        })),
    })).filter((s) => s.positionen.length > 0);
  }, [libraryPositions, pickerSearch]);

  const libraryById = useMemo(() => {
    const map = new Map<string, LibraryPosition>();
    libraryPositions.forEach((lp) => map.set(`lib-${lp.id}`, lp));
    return map;
  }, [libraryPositions]);

  // IDs der Positionen, die tatsächlich als auswählbare Einträge im Picker
  // erscheinen (nur aktive Bibliotheks-Einträge). Bewusst nicht libraryById,
  // damit inaktive/deaktivierte Library-Positionen ebenfalls als „außerhalb
  // der Bibliothek" sichtbar werden, statt unsichtbar zu sein.
  const pickerSourceIds = useMemo(() => {
    const ids = new Set<string>();
    libraryPositions.forEach((lp) => {
      if (lp.aktiv) ids.add(`lib-${lp.id}`);
    });
    return ids;
  }, [libraryPositions]);

  // Positionen am Foto, die nicht aus der Picker-Quelle stammen (eigene,
  // Legacy oder inzwischen deaktivierte Library-Einträge). Werden im Picker
  // als nicht ab-/anwählbarer Hinweis-Block angezeigt, damit erkennbar bleibt,
  // was bereits am Foto hängt.
  const foreignPhotoPositionen = useMemo(
    () => photoPositionen.filter((p) => !pickerSourceIds.has(p.id)),
    [photoPositionen, pickerSourceIds],
  );

  const openPicker = () => {
    // pickerSelection nur auf IDs beschränken, die tatsächlich in der
    // Picker-Quelle (Bibliothek) vorkommen. Quell-fremde Positionen erscheinen
    // nicht im Dialog und dürfen nicht als „abgewählt" interpretiert werden.
    const alreadySelected = new Set(
      photoPositionen.filter((p) => libraryById.has(p.id)).map((p) => p.id),
    );
    setPickerSelection(alreadySelected);
    setPickerSearch("");
    setPickerOpen(true);
  };

  const confirmPicker = () => {
    const existingMap = new Map(photoPositionen.map((p) => [p.id, p]));
    const newPositionen: PhotoLVPosition[] = [];
    // Vorschlag: gemessene Fläche aus Aufmaß; sonst 0 (kein implizites 1).
    const defaultQty = measuredArea && measuredArea > 0 ? Math.round(measuredArea * 100) / 100 : 0;

    // Alle Positionen, deren ID nicht in der Picker-Quelle vorkommt, werden
    // unverändert erhalten — unabhängig vom Auswahl-Status. Damit bleiben
    // eigene/Legacy-Positionen sowie Bibliotheks-Positionen, die inzwischen
    // deaktiviert/gelöscht wurden, nach dem Bestätigen erhalten.
    photoPositionen.forEach((p) => {
      if (!libraryById.has(p.id)) {
        newPositionen.push(p);
      }
    });

    pickerSelection.forEach((selectedId) => {
      const lp = libraryById.get(selectedId);
      const existing = existingMap.get(selectedId);
      if (!lp) {
        // Library-Eintrag (noch) nicht geladen — bestehende Auswahl
        // unverändert erhalten, damit kein Datenverlust entsteht. Neue,
        // unauflösbare IDs werden ignoriert.
        if (existing) newPositionen.push(existing);
        return;
      }
      if (existing) {
        // Vorhandene Position behalten — Nutzer-Eingaben (qty) nicht überschreiben.
        // Name/Einheit/EP aus Bibliothek aktualisieren, damit Änderungen am
        // Stamm sofort beim nächsten Öffnen wirken.
        newPositionen.push({
          ...existing,
          name: lp.name,
          unit: lp.unit,
          ep: lp.ep,
          berechneteMenge: defaultQty > 0 ? defaultQty : existing.berechneteMenge,
        });
      } else {
        newPositionen.push({
          id: selectedId,
          name: lp.name,
          unit: lp.unit,
          ep: lp.ep,
          qty: defaultQty,
          berechneteMenge: defaultQty > 0 ? defaultQty : undefined,
        });
      }
    });

    onPositionenChange(newPositionen);
    setPickerOpen(false);
  };

  const resetQtyToBerechnet = (posId: string) => {
    onPositionenChange(
      photoPositionen.map((p) =>
        p.id === posId && p.berechneteMenge !== undefined ? { ...p, qty: p.berechneteMenge } : p
      )
    );
  };

  const updateQty = (posId: string, val: string) => {
    const qty = Math.max(0, parseFloat(val.replace(",", ".")) || 0);
    onPositionenChange(
      photoPositionen.map((p) => (p.id === posId ? { ...p, qty } : p))
    );
  };

  const updateUnit = (posId: string, unit: string) => {
    onPositionenChange(
      photoPositionen.map((p) => (p.id === posId ? { ...p, unit } : p))
    );
  };

  const updateEp = (posId: string, val: string) => {
    const ep = Math.max(0, parseFloat(val.replace(",", ".")) || 0);
    onPositionenChange(
      photoPositionen.map((p) => (p.id === posId ? { ...p, ep } : p))
    );
  };

  const toggleVariant = (posId: string) => {
    onPositionenChange(
      photoPositionen.map((p) => (p.id === posId ? { ...p, variant: !p.variant } : p))
    );
  };

  const removePosition = (posId: string) => {
    onPositionenChange(photoPositionen.filter((p) => p.id !== posId));
  };

  const togglePickerPosition = (posId: string) => {
    setPickerSelection((prev) => {
      const next = new Set(prev);
      next.has(posId) ? next.delete(posId) : next.add(posId);
      return next;
    });
  };

  const copySources = useMemo(
    () => (otherPhotosWithPositions ?? []).filter((s) => s.positionen.length > 0),
    [otherPhotosWithPositions],
  );

  const openCopyDialog = () => {
    if (copySources.length === 0) return;
    setCopySourceId((prev) => {
      if (prev && copySources.some((s) => s.photoId === prev)) return prev;
      return copySources[0]?.photoId ?? null;
    });
    setCopyMode(photoPositionen.length === 0 ? "replace" : "append");
    setCopyOpen(true);
  };

  const applyCopy = () => {
    const source = copySources.find((s) => s.photoId === copySourceId);
    if (!source) {
      setCopyOpen(false);
      return;
    }
    // Deep-Clone der Quelle, damit spätere Bearbeitungen am Ziel-Foto die
    // Quell-Positionen nicht versehentlich ändern (gleiche Object-Refs!).
    const cloned: PhotoLVPosition[] = source.positionen.map((p) => ({ ...p }));
    if (copyMode === "replace") {
      onPositionenChange(cloned);
    } else {
      // Anhängen: vorhandene IDs nicht überschreiben (Nutzer-Mengen behalten).
      const existingIds = new Set(photoPositionen.map((p) => p.id));
      const additions = cloned.filter((p) => !existingIds.has(p.id));
      onPositionenChange([...photoPositionen, ...additions]);
    }
    setCopyOpen(false);
  };

  const hasData = description.trim().length > 0 || photoPositionen.length > 0;

  return (
    <div className="border border-border rounded-lg bg-card mt-2">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-muted/40 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
          <span className="text-sm font-medium text-foreground">
            Schadenserfassung
          </span>
          {hasData && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">
              {photoPositionen.length > 0
                ? `${photoPositionen.length} Pos. · € ${formatAmount(subtotal)}`
                : "Beschreibung vorhanden"}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
        )}
      </button>

      {isOpen && (
        <div className="border-t border-border px-3 py-3 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Schadensbeschreibung</Label>
            <Textarea
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              placeholder="z.B. Punktuelle Ausbesserung Ecke Nord, Rissbildung Sturz..."
              rows={2}
              className="text-sm focus:ring-bauwerk-teal focus:border-bauwerk-teal"
            />
          </div>

          {photoPositionen.length > 0 && (
            <div className="space-y-2">
              <div className="grid grid-cols-[minmax(0,1fr)_4rem_3.6rem_4rem_4.5rem_1.5rem_1.5rem] gap-1 px-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                <span>Leistung</span>
                <span className="text-right">Menge</span>
                <span className="text-right">Einheit</span>
                <span className="text-right">EP €</span>
                <span className="text-right">Gesamt</span>
                <span />
                <span />
              </div>
              {photoPositionen.map((pos) => {
                const hasBerechnet = pos.berechneteMenge !== undefined && pos.berechneteMenge > 0;
                const isOverridden = hasBerechnet && Math.abs((pos.berechneteMenge ?? 0) - pos.qty) > 0.005;
                const isVariant = !!pos.variant;
                // Veraltet/freier Eintrag: ID gehört nicht zur Bibliothek (z.B.
                // alte hartkodierte "pX"-IDs ohne Match) → Hinweis anzeigen,
                // damit der Nutzer entscheiden kann, ob er die Position löscht
                // und eine passende aus der Bibliothek wählt.
                const isOrphan =
                  !pos.id.startsWith("lib-") && !isLibraryLoading && !isLibraryError;
                return (
                  <div
                    key={pos.id}
                    className={`space-y-0.5 rounded-md ${isVariant ? "bg-amber-50/40 border border-amber-200 px-1 py-0.5" : ""}`}
                    data-testid={`photo-pos-row-${pos.id}`}
                  >
                    <div
                      className="grid grid-cols-[minmax(0,1fr)_4rem_3.6rem_4rem_4.5rem_1.5rem_1.5rem] gap-1 items-center"
                    >
                      <div className="min-w-0 flex items-center gap-1">
                        <span className="text-xs truncate" title={pos.name}>
                          {pos.name}
                        </span>
                        {isVariant && (
                          <span
                            className="text-[9px] px-1 py-0.5 rounded bg-amber-200 text-amber-900 font-semibold shrink-0"
                            title="Variante – nicht in Summe enthalten"
                          >
                            VAR
                          </span>
                        )}
                        {isOrphan && (
                          <span
                            className="text-[9px] px-1 py-0.5 rounded bg-orange-200 text-orange-900 font-semibold shrink-0"
                            title={`Veraltet/freier Eintrag – kein passender Treffer in der Positions-Bibliothek. Bei Bedarf löschen und über „Positionen hinzufügen" neu aus der Bibliothek wählen.`}
                            data-testid={`photo-pos-orphan-${pos.id}`}
                          >
                            FREI
                          </span>
                        )}
                      </div>
                      <Input
                        type="number"
                        value={pos.qty}
                        min={0}
                        step="any"
                        onChange={(e) => updateQty(pos.id, e.target.value)}
                        className="h-7 text-xs text-right px-1.5 py-1"
                        data-testid={`photo-pos-qty-${pos.id}`}
                      />
                      <select
                        value={STANDARD_EINHEITEN.includes(pos.unit as typeof STANDARD_EINHEITEN[number]) ? pos.unit : "__custom__"}
                        onChange={(e) => {
                          if (e.target.value === "__custom__") {
                            const v = window.prompt("Eigene Einheit eingeben:", pos.unit);
                            if (v && v.trim()) updateUnit(pos.id, v.trim());
                          } else {
                            updateUnit(pos.id, e.target.value);
                          }
                        }}
                        className="h-7 w-full text-xs text-right px-1 rounded border border-input bg-background focus:outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
                        data-testid={`photo-pos-unit-${pos.id}`}
                      >
                        {STANDARD_EINHEITEN.includes(pos.unit as typeof STANDARD_EINHEITEN[number]) ? null : (
                          <option value="__custom__">{pos.unit}</option>
                        )}
                        {STANDARD_EINHEITEN.map((u) => (
                          <option key={u} value={u}>{u}</option>
                        ))}
                        <option value="__custom__">Andere…</option>
                      </select>
                      <Input
                        type="number"
                        value={pos.ep}
                        min={0}
                        step="any"
                        onChange={(e) => updateEp(pos.id, e.target.value)}
                        className="h-7 text-xs text-right px-1.5 py-1"
                        data-testid={`photo-pos-ep-${pos.id}`}
                      />
                      <span className={`text-xs text-right tabular-nums font-medium ${isVariant ? "text-amber-700 line-through opacity-70" : ""}`}>
                        € {formatAmount(pos.qty * pos.ep)}
                      </span>
                      <button
                        type="button"
                        onClick={() => toggleVariant(pos.id)}
                        title={isVariant ? "Als reguläre Position markieren" : "Als Variante markieren (zählt nicht zur Summe)"}
                        className={`p-0.5 rounded transition-colors ${isVariant ? "text-amber-700 hover:text-amber-800" : "text-muted-foreground hover:text-amber-700"}`}
                        data-testid={`photo-pos-variant-${pos.id}`}
                      >
                        <GitCompare className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removePosition(pos.id)}
                        className="text-muted-foreground hover:text-destructive p-0.5 rounded transition-colors"
                        data-testid={`photo-pos-remove-${pos.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {(hasBerechnet || (onJumpToLv && !isOrphan)) && (
                      <div className="flex items-center justify-between gap-2 text-[10px] text-muted-foreground pl-1">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          {hasBerechnet && (
                            <span className="truncate">
                              berechnet aus Aufmaß: {pos.berechneteMenge!.toFixed(2)} {pos.unit}
                              {isOverridden && (
                                <span className="ml-1 text-amber-700">· übernommen: {pos.qty.toFixed(2)} {pos.unit}</span>
                              )}
                            </span>
                          )}
                          {hasBerechnet && (
                            <button
                              type="button"
                              onClick={() => resetQtyToBerechnet(pos.id)}
                              disabled={!isOverridden}
                              title="Menge auf berechneten Wert zurücksetzen"
                              className="inline-flex items-center gap-1 text-[10px] text-emerald-700 hover:text-emerald-800 disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
                            >
                              <RotateCcw className="w-2.5 h-2.5" />
                              Aufmaß übernehmen
                            </button>
                          )}
                        </div>
                        {onJumpToLv && !isOrphan && (
                          <button
                            type="button"
                            onClick={() => onJumpToLv(pos.id)}
                            title="Zu Schritt 4 (Leistungsverzeichnis) springen und diese Position hervorheben"
                            className="inline-flex items-center gap-1 text-[10px] text-bauwerk-teal hover:underline shrink-0"
                            data-testid={`photo-pos-jump-lv-${pos.id}`}
                          >
                            Im LV anzeigen
                            <ArrowRight className="w-2.5 h-2.5" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="flex justify-between items-center border-t border-border pt-2 mt-1">
                <span className="text-xs font-medium text-muted-foreground">
                  Zwischensumme
                </span>
                <span className="text-sm font-semibold tabular-nums text-emerald-700">
                  € {formatAmount(subtotal)}
                </span>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={openPicker}
              className="flex-1 border-dashed border-amber-300 text-amber-700 hover:bg-amber-50 hover:border-amber-400"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Positionen hinzufügen
            </Button>
            {copySources.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={openCopyDialog}
                className="flex-1 border-dashed border-bauwerk-teal/50 text-bauwerk-teal hover:bg-bauwerk-mint hover:border-bauwerk-teal"
                data-testid={`photo-pos-copy-from-${photoId}`}
              >
                <Copy className="w-3.5 h-3.5 mr-1.5" />
                Aus anderem Foto übernehmen
              </Button>
            )}
          </div>
        </div>
      )}

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-bauwerk-teal">
              LV-Positionen auswählen
            </DialogTitle>
            <p className="text-xs text-muted-foreground mt-0.5">{photoName}</p>
          </DialogHeader>

          <div className="pb-2">
            <Input
              type="search"
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
              placeholder="Positionen durchsuchen (Name, Tag, Einheit)…"
              className="h-8 text-sm"
              data-testid="picker-search"
              disabled={isLibraryLoading || isLibraryError}
            />
          </div>

          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            {isLibraryLoading ? (
              <div className="space-y-3 py-2" data-testid="picker-loading">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Positions-Bibliothek wird geladen…
                </div>
                {[0, 1, 2].map((i) => (
                  <div key={i} className="space-y-1.5">
                    <div className="h-3 w-40 bg-muted/60 rounded animate-pulse" />
                    <div className="h-7 w-full bg-muted/40 rounded animate-pulse" />
                    <div className="h-7 w-full bg-muted/40 rounded animate-pulse" />
                  </div>
                ))}
              </div>
            ) : isLibraryError ? (
              <div
                className="flex flex-col items-center justify-center text-center gap-2 py-8 px-4 rounded-md border border-dashed border-destructive/40 bg-destructive/5"
                data-testid="picker-error"
              >
                <AlertCircle className="w-8 h-8 text-destructive/70" />
                <div className="text-sm font-medium">Bibliothek konnte nicht geladen werden</div>
                <p className="text-xs text-muted-foreground max-w-sm">
                  Bitte schließe den Dialog und versuche es erneut. Eine Übernahme
                  ist im Moment nicht möglich, damit keine Positionen verloren gehen.
                </p>
              </div>
            ) : (
              <>
                {foreignPhotoPositionen.length > 0 && (
                  <div data-testid="picker-foreign-section">
                    <div className="flex items-center gap-1.5 mb-1.5 sticky top-0 bg-background py-1 z-10">
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Bereits am Foto (außerhalb der Bibliothek)
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mb-1.5 px-0.5">
                      Diese Positionen sind nicht Teil der Positions-Bibliothek
                      (eigene oder ältere Einträge). Sie können hier nicht
                      ab-/angewählt werden — bearbeite oder entferne sie direkt
                      in der Positionsliste am Foto.
                    </p>
                    <div className="space-y-1">
                      {foreignPhotoPositionen.map((pos) => (
                        <div
                          key={pos.id}
                          className="flex items-start gap-2.5 rounded-md px-2 py-1.5 border border-dashed border-amber-300/70 bg-amber-50/40 opacity-90"
                          data-testid={`picker-foreign-${pos.id}`}
                        >
                          <Checkbox
                            checked
                            disabled
                            aria-label="Position außerhalb der Bibliothek"
                            className="mt-0.5 shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-sm leading-snug">{pos.name}</span>
                              <span className="text-[9px] px-1 py-0.5 rounded bg-amber-200 text-amber-900 font-semibold shrink-0">
                                NICHT IN BIBLIOTHEK
                              </span>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {pos.unit} · € {formatAmount(pos.ep)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {pickerSections.length === 0 ? (
                  pickerSearch.trim() ? (
                    <div
                      className="flex flex-col items-center justify-center text-center gap-2 py-8 px-4 rounded-md border border-dashed border-muted-foreground/30"
                      data-testid="picker-no-results"
                    >
                      <BookOpen className="w-8 h-8 text-muted-foreground/60" />
                      <div className="text-sm font-medium">Keine Treffer für „{pickerSearch}"</div>
                      <p className="text-xs text-muted-foreground max-w-sm">
                        Passe den Suchbegriff an oder lösche das Suchfeld, um wieder
                        alle Positionen zu sehen.
                      </p>
                    </div>
                  ) : (
                    <div
                      className="flex flex-col items-center justify-center text-center gap-2 py-8 px-4 rounded-md border border-dashed border-muted-foreground/30"
                      data-testid="picker-empty"
                    >
                      <BookOpen className="w-8 h-8 text-muted-foreground/60" />
                      <div className="text-sm font-medium">Noch keine Positionen in der Bibliothek</div>
                      <p className="text-xs text-muted-foreground max-w-sm">
                        Lege deine Leistungspositionen einmalig in der Positions-Bibliothek
                        an (Menü „Positions-Bibliothek"). Sobald dort aktive Einträge
                        vorhanden sind, kannst du sie hier auswählen.
                      </p>
                    </div>
                  )
                ) : (
                  pickerSections.map((sektion) => (
                <div key={sektion.id}>
                  <div className="flex items-center gap-1.5 mb-1.5 sticky top-0 bg-background py-1 z-10">
                    <span className="text-base">{sektion.icon}</span>
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {sektion.titel}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {sektion.positionen.map((pos) => {
                      const checked = pickerSelection.has(pos.id);
                      return (
                        <label
                          key={pos.id}
                          className={`flex items-start gap-2.5 rounded-md px-2 py-1.5 cursor-pointer transition-colors ${
                            checked
                              ? "bg-emerald-50 border border-emerald-200"
                              : "hover:bg-muted/40 border border-transparent"
                          }`}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => togglePickerPosition(pos.id)}
                            className="mt-0.5 shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm leading-snug">{pos.name}</span>
                            <span className="text-xs text-muted-foreground ml-1.5">
                              {pos.unit} · € {formatAmount(pos.ep)}
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
                  ))
                )}
              </>
            )}
          </div>

          <DialogFooter className="pt-3 border-t border-border">
            <div className="flex items-center justify-between w-full">
              <span className="text-xs text-muted-foreground">
                {pickerSelection.size} Position(en) ausgewählt
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPickerOpen(false)}
                >
                  Abbrechen
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={confirmPicker}
                  disabled={isLibraryLoading || isLibraryError}
                  className="bg-bauwerk-teal hover:bg-bauwerk-teal/90 text-white"
                >
                  Übernehmen
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-bauwerk-teal">
              Positionen aus anderem Foto übernehmen
            </DialogTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              Ziel: {photoName}
            </p>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Quell-Foto
              </Label>
              <div className="space-y-1 max-h-60 overflow-y-auto pr-1">
                {copySources.map((source) => {
                  const subtotal = source.positionen.reduce(
                    (sum, p) => (p.variant ? sum : sum + p.qty * p.ep),
                    0,
                  );
                  const checked = copySourceId === source.photoId;
                  return (
                    <label
                      key={source.photoId}
                      className={`flex items-start gap-2.5 rounded-md px-2 py-2 cursor-pointer transition-colors border ${
                        checked
                          ? "bg-bauwerk-mint/40 border-bauwerk-teal"
                          : "border-transparent hover:bg-muted/40"
                      }`}
                      data-testid={`copy-source-${source.photoId}`}
                    >
                      <input
                        type="radio"
                        name={`copy-source-${photoId}`}
                        checked={checked}
                        onChange={() => setCopySourceId(source.photoId)}
                        className="mt-1 shrink-0 accent-bauwerk-teal"
                      />
                      <div className="flex-1 min-w-0">
                        <div
                          className="text-sm font-medium truncate"
                          title={source.photoName}
                        >
                          {source.photoName}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {source.positionen.length} Pos. · € {formatAmount(subtotal)}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Modus
              </Label>
              <div className="space-y-1">
                <label
                  className={`flex items-start gap-2.5 rounded-md px-2 py-2 cursor-pointer transition-colors border ${
                    copyMode === "append"
                      ? "bg-bauwerk-mint/40 border-bauwerk-teal"
                      : "border-transparent hover:bg-muted/40"
                  }`}
                >
                  <input
                    type="radio"
                    name={`copy-mode-${photoId}`}
                    checked={copyMode === "append"}
                    onChange={() => setCopyMode("append")}
                    className="mt-1 shrink-0 accent-bauwerk-teal"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">Anhängen</div>
                    <div className="text-xs text-muted-foreground">
                      Neue Positionen werden ergänzt. Bestehende bleiben unverändert
                      (keine Duplikate).
                    </div>
                  </div>
                </label>
                <label
                  className={`flex items-start gap-2.5 rounded-md px-2 py-2 cursor-pointer transition-colors border ${
                    copyMode === "replace"
                      ? "bg-bauwerk-mint/40 border-bauwerk-teal"
                      : "border-transparent hover:bg-muted/40"
                  }`}
                >
                  <input
                    type="radio"
                    name={`copy-mode-${photoId}`}
                    checked={copyMode === "replace"}
                    onChange={() => setCopyMode("replace")}
                    className="mt-1 shrink-0 accent-bauwerk-teal"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">Ersetzen</div>
                    <div className="text-xs text-muted-foreground">
                      Alle bestehenden Positionen am aktuellen Foto werden
                      überschrieben.
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <p className="text-[11px] text-muted-foreground bg-muted/40 rounded px-2 py-1.5">
              Mengen werden 1:1 vom Quell-Foto übernommen. Bei Bedarf danach
              pro Position über „Aufmaß übernehmen" auf den Wert dieses
              Fotos zurücksetzen.
            </p>
          </div>

          <DialogFooter className="pt-3 border-t border-border">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setCopyOpen(false)}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={applyCopy}
              disabled={!copySourceId}
              className="bg-bauwerk-teal hover:bg-bauwerk-teal/90 text-white"
              data-testid="copy-apply"
            >
              Übernehmen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
